/**
 * utilities/usePermissions.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Client-side RBAC gate for the dashboard menu + routes. Companion to the
 * server-side effective-permission resolver exposed at
 *   GET /api/rbac/me/permissions
 *      → { success, data: { user_role, isSuperUser, permissions } }
 *
 * `permissions` is ["*"] for a SUPERUSER (full access), otherwise an array of
 * `domain:action` keys representing the user's EFFECTIVE permissions (role
 * defaults merged with any per-user GRANT/DENY overrides).
 *
 * Exposes a context provider (PermissionsProvider) + a hook (usePermissions)
 * returning:
 *
 *   - can(key)        Boolean — membership test for a single key. Accepts a
 *                     full "domain:action" key OR a bare "domain" (domain-only
 *                     check → true if the user has ANY action in that domain).
 *   - canAny(keys)    Boolean — true if can() is true for at least one key.
 *   - isSuperUser     Boolean — true when permissions include "*".
 *   - userRole        String  — the user's role label from the API.
 *   - permissions     Array   — the raw effective key list (["*"] for super).
 *   - loading         Boolean — true until the first fetch resolves.
 *   - error           String  — last fetch error message (or null).
 *   - refresh()       Re-fetch from the server.
 *
 * IMPORTANT — fail-OPEN while loading. Until we have a definitive loaded set,
 * can()/canAny() return TRUE so the sidebar doesn't flash-hide every menu item
 * during the initial fetch. Items are only hidden once we have a resolved
 * permission set that demonstrably lacks the key. SUPERUSER ("*") always
 * passes. If the fetch errors we also fail open (treat as permitted) so a
 * transient API blip never locks an authorised user out of their own menu —
 * the backend remains the hard enforcement boundary.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRbacMyPermissions } from "../api/index.js";
import { getUserFromLocalStorage } from "./user.js";
import { getTokenFromLocalStorage, isTokenExpired } from "./tokenUtils.js";

const PermissionsContext = createContext(null);

const SUPER_WILDCARD = "*";

// Normalise whatever the API returns into a predictable array of strings.
const toKeyArray = (perms) => {
  if (!Array.isArray(perms)) return [];
  return perms.filter((p) => typeof p === "string" && p.length > 0);
};

const hasWildcard = (perms) => toKeyArray(perms).includes(SUPER_WILDCARD);

/**
 * Pure membership test against a resolved key set.
 *   - "*" present → always true.
 *   - "domain:action" → exact membership.
 *   - "domain" (no colon) → true if ANY key starts with "domain:".
 */
const keyMatches = (permissions, key) => {
  if (!key || typeof key !== "string") return true; // unkeyed → visible
  const keys = toKeyArray(permissions);
  if (keys.includes(SUPER_WILDCARD)) return true;
  if (keys.includes(key)) return true;
  if (!key.includes(":")) {
    // Domain-only check — does the user hold any action in this domain?
    const prefix = `${key}:`;
    return keys.some((k) => k.startsWith(prefix));
  }
  return false;
};

export const PermissionsProvider = ({ children }) => {
  const [state, setState] = useState({
    loading: true,
    error: null,
    loaded: false, // becomes true once we have a definitive set from the API
    isSuperUser: false,
    userRole: null,
    permissions: null, // null until resolved; ["*"] for super, else key array
  });

  const load = useCallback(async () => {
    try {
      const token = getTokenFromLocalStorage();
      if (!token || isTokenExpired(token)) {
        // No session — nothing to fetch. Router will redirect to /auth.
        setState((s) => ({ ...s, loading: false }));
        return;
      }

      // Optimistic local hint from the cached JWT payload so the first paint
      // can already treat a known SUPERUSER as full-access without waiting.
      const localUser = getUserFromLocalStorage();
      const localRole = String(localUser?.result?.user_role || "").toUpperCase();
      if (localRole === "SUPERUSER") {
        setState((s) => ({ ...s, isSuperUser: true }));
      }

      // A 3PL identity is not a `Users` row: a partner is a ThreePLPartner and
      // their staff are ThreePLStaff. Both endpoints below resolve the caller
      // with `Users.findById`, so for these accounts they can only ever answer
      // 401 and 404. Asking anyway put two red errors in every partner's
      // console on every page load and taught them to ignore the console.
      const mode = localUser?.result?.operationMode;
      if (mode === "3PL_PARTNER" || mode === "3PL_STAFF") {
        // Leave `loaded` false so can() keeps failing open, exactly as it did
        // when the request was made and refused. 3PL menus are gated by
        // Admin.js's own staff-permission filter, not by this hook.
        setState((s) => ({ ...s, loading: false }));
        return;
      }

      const { data } = await apiRbacMyPermissions();
      if (data && data.success && data.data) {
        const perms = toKeyArray(data.data.permissions);
        setState({
          loading: false,
          error: null,
          loaded: true,
          isSuperUser: Boolean(data.data.isSuperUser) || hasWildcard(perms),
          userRole: data.data.user_role || null,
          permissions: perms,
        });
      } else {
        // Unexpected payload — stop loading but DON'T mark loaded, so we keep
        // failing open rather than hiding the whole menu on a bad response.
        setState((s) => ({
          ...s,
          loading: false,
          error: "Permission fetch returned an unexpected payload.",
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[usePermissions] fetch failed:", err?.message);
      // Fail open: leave `loaded` false so can() keeps returning true. The
      // backend is the real gate; we never lock a user out of their own menu
      // because of a transient client-side fetch error.
      setState((s) => ({
        ...s,
        loading: false,
        error: err?.response?.data?.message || err?.message || "Fetch failed",
      }));
    }
  }, []);

  useEffect(() => {
    load();
    // Re-fetch if the session changes in another tab.
    const onStorage = (e) => {
      if (e.key === "token" || e.key === "profile") load();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [load]);

  const value = useMemo(() => {
    const { loaded, isSuperUser, permissions, userRole, loading, error } = state;

    const can = (key) => {
      if (isSuperUser) return true; // SUPERUSER bypass
      // Fail open until we have a definitive set (covers loading + error/
      // unexpected-payload states). Only enforce once `loaded` is true.
      if (!loaded) return true;
      return keyMatches(permissions, key);
    };

    const canAny = (keys) => {
      if (isSuperUser) return true;
      if (!loaded) return true;
      if (!Array.isArray(keys) || keys.length === 0) return true;
      return keys.some((k) => keyMatches(permissions, k));
    };

    return {
      loading,
      error,
      loaded,
      isSuperUser,
      userRole,
      permissions,
      can,
      canAny,
      refresh: load,
    };
  }, [state, load]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook — reads from PermissionsContext. Returns a fail-OPEN shape if called
 * outside the provider (never throws; can()/canAny() return true) so a stray
 * mount can never hard-crash or blank the dashboard.
 */
export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    return {
      loading: false,
      error: "PermissionsProvider missing from tree",
      loaded: false,
      isSuperUser: false,
      userRole: null,
      permissions: null,
      can: () => true,
      canAny: () => true,
      refresh: () => {},
    };
  }
  return ctx;
};

export default PermissionsContext;
